import { Alert, Box, Button, Chip, TextField, Typography } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isEmpty, isNil } from "lodash";
import { useState } from "react";
import { useImportCrownTariffMutation } from "../../../generated/graphql";
import type { ImportTariffFormValuesV2 } from "../../tariff-importer/use-import-tariff-form";
import { useCrownEnv } from "../env/env-context";

const PROD_CONFIRM_PHRASE = "PROD";

// Mirrors the backend `CROWN_DRY_RUN_UNAVAILABLE_CODE` (crown-data.resolver.ts).
const DRY_RUN_UNAVAILABLE_CODE = "CROWN_DRY_RUN_UNAVAILABLE";

type GraphqlErrorLike = {
  readonly extensions?: { readonly code?: unknown } | null;
};

/**
 * True when a GraphQL error response carries the stable
 * "dry-run unavailable" code, signalling the wizard to fall back to
 * direct-commit mode instead of surfacing a hard error.
 *
 * @param errors The `errors` array from an Apollo mutation result.
 * @returns Whether any error matches the dry-run-unavailable code.
 */
const hasDryRunUnavailableError = (
  errors: readonly GraphqlErrorLike[] | undefined,
): boolean =>
  errors?.some(
    (error) => error.extensions?.code === DRY_RUN_UNAVAILABLE_CODE,
  ) ?? false;

const styles = {
  header: { display: "flex", alignItems: "center", gap: 1, marginBottom: 2 },
  summary: { marginBottom: 2 },
  section: { marginTop: 2 },
  actions: { display: "flex", alignItems: "center", gap: 2, marginTop: 2 },
  confirmInput: { marginTop: 2, maxWidth: 320 },
  danger: {
    border: "2px solid",
    borderColor: "error.main",
    borderRadius: 1,
    padding: 2,
  },
} satisfies Record<string, SxProps<Theme>>;

type DryRunResult = {
  success: boolean;
  error?: string | null;
  tariffsToCreate?: number | null;
  rangesToCreate?: number | null;
};

type ReviewStepProps = {
  readonly values: ImportTariffFormValuesV2;
  readonly companyName: string;
  readonly dryRunSupported: boolean;
  readonly onImported: () => void;
  readonly validateAll: () => Promise<boolean>;
};

/**
 * Renders the final wizard step that previews and commits a Crown tariff
 * import.
 *
 * Shows a summary of the captured form values, the target environment, and the
 * import controls. When the line-haul-networks gate is enabled
 * (`dryRunSupported`), a dry-run preview must succeed before the real import is
 * allowed; otherwise a warning is shown and the import commits directly. When
 * the selected env is production, a typed "PROD" confirmation is always
 * required before the import button is enabled (plus a successful dry-run only
 * when dry-run is supported), and the step is wrapped in a red danger border.
 *
 * Side effects: calls the `importCrownTariff` mutation (with `dryRun: true` for
 * the preview and `dryRun: false` for the commit). On a successful commit it
 * invokes `onImported`.
 *
 * @param props.values The current tariff form values to import.
 * @param props.companyName The display name of the company being imported into.
 * @param props.dryRunSupported Whether a dry-run preview is available (backend
 *   line-haul-networks gate enabled).
 * @param props.onImported Called once the real import completes without error.
 * @param props.validateAll Re-validates the entire form (across all earlier
 *   wizard steps); the commit is blocked when it resolves to `false`.
 */
export const ReviewStep = ({
  values,
  companyName,
  dryRunSupported,
  onImported,
  validateAll,
}: ReviewStepProps) => {
  const { env } = useCrownEnv();
  const isProd = env === "prod";

  const [importCrownTariff, { loading }] = useImportCrownTariffMutation();

  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const [dryRunUnavailable, setDryRunUnavailable] = useState(false);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);

  const buildInput = (isDryRun: boolean) => ({
    contactUuids: values.contactUuids,
    serviceUuids: values.serviceUuids ?? [],
    vehicleTypeUuids: values.vehicleTypeUuids ?? [],
    name: values.name,
    tariffZoneType: values.tariffZoneType,
    tariffType: values.tariffType,
    rateType: values.rateType,
    tariffGroupType: values.tariffGroupType,
    tariffZoneGroupId: values.tariffZoneGroupId,
    tariffUrl: values.tariffUrl,
    useActualWeight: values.useActualWeight,
    autoSelectAllServices: values.autoSelectAllServices,
    autoSelectAllVehicleTypes: values.autoSelectAllVehicleTypes,
    startDate:
      values.startDate === null ||
      values.startDate === undefined ||
      values.startDate === ""
        ? null
        : new Date(values.startDate),
    endDate:
      values.endDate === null ||
      values.endDate === undefined ||
      values.endDate === ""
        ? null
        : new Date(values.endDate),
    fuelProfileUuid: values.fuelProfileUuid,
    dryRun: isDryRun,
  });

  const runDryRun = async () => {
    setError(null);
    try {
      const response = await importCrownTariff({
        variables: { importCrownTariffInput: buildInput(true) },
      });
      // The backend rejects dry-run for companies whose line-haul-networks flag
      // is off (the legacy importer cannot preview), tagged with a stable error
      // code. Match the code (not the message text) and fall back to
      // direct-commit mode rather than showing a hard error.
      if (hasDryRunUnavailableError(response.errors)) {
        setDryRunUnavailable(true);
        setDryRun(null);
        return;
      }
      const result = response.data?.importCrownTariff;
      if (isNil(result)) {
        setDryRun(null);
        setError(response.errors?.[0]?.message ?? "No response from preview");
        return;
      }
      setDryRun({
        success: result.success,
        error: result.error,
        tariffsToCreate: result.tariffsToCreate,
        rangesToCreate: result.rangesToCreate,
      });
    } catch (error_) {
      setDryRun(null);
      setError(`Error: ${String(error_)}`);
    }
  };

  const runImport = async () => {
    setError(null);
    const valid = await validateAll();
    if (!valid) {
      setError("Please fix the highlighted fields in earlier steps.");
      return;
    }
    try {
      const response = await importCrownTariff({
        variables: { importCrownTariffInput: buildInput(false) },
      });
      const importError = response.data?.importCrownTariff?.error;
      if (isNil(importError)) {
        onImported();
      } else {
        setError(importError);
      }
    } catch (error_) {
      setError(`Error: ${String(error_)}`);
    }
  };

  const dryRunSucceeded = dryRun?.success === true;

  // Falls back to false once the backend reports dry-run is unavailable for this
  // company, so the UI stops requiring/offering a preview that cannot run.
  const effectiveDryRunSupported = dryRunSupported && !dryRunUnavailable;
  const needsDryRun = effectiveDryRunSupported && !dryRunSucceeded;
  const needsProdConfirm = isProd && typed !== PROD_CONFIRM_PHRASE;
  const importDisabled = loading || needsDryRun || needsProdConfirm;

  const content = (
    <Box>
      <Box sx={styles.header}>
        <Typography variant="h6">Importing to {env.toUpperCase()}</Typography>
        {isProd && <Chip color="error" label="PROD" />}
        <Typography variant="h6">· {companyName}</Typography>
      </Box>

      <Box sx={styles.summary}>
        <Typography variant="body2">
          File uploaded: {isEmpty(values.tariffUrl) ? "No" : "Yes"}
        </Typography>
        <Typography variant="body2">Name: {values.name}</Typography>
        <Typography variant="body2">Group: {values.tariffGroupType}</Typography>
        <Typography variant="body2">Zone: {values.tariffZoneType}</Typography>
        <Typography variant="body2">Type: {values.tariffType}</Typography>
        <Typography variant="body2">Rate: {values.rateType}</Typography>
      </Box>

      {effectiveDryRunSupported ? (
        <Box sx={styles.section}>
          <Button
            data-testid="run-dry-run"
            disabled={loading}
            variant="outlined"
            onClick={runDryRun}
          >
            Run preview (dry-run)
          </Button>
          {dryRunSucceeded && (
            <Alert
              data-testid="dry-run-result"
              severity="success"
              sx={styles.section}
            >
              Will create {dryRun?.tariffsToCreate ?? 0} tariffs and{" "}
              {dryRun?.rangesToCreate ?? 0} ranges
            </Alert>
          )}
          {!isNil(dryRun) && !dryRun.success && (
            <Alert severity="error" sx={styles.section}>
              {dryRun.error ?? "Preview failed"}
            </Alert>
          )}
        </Box>
      ) : (
        <Alert severity="warning" sx={styles.section}>
          Preview unavailable for this company (line-haul networks off).
          {isProd
            ? ' For prod, type "PROD" to confirm a direct commit.'
            : " The import will commit directly."}
        </Alert>
      )}

      {isProd && (
        <TextField
          data-testid="prod-confirm-input"
          label='Type "PROD" to confirm'
          size="small"
          sx={styles.confirmInput}
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
          }}
        />
      )}

      <Box sx={styles.actions}>
        <Button
          data-testid="run-import"
          color={isProd ? "error" : "primary"}
          disabled={importDisabled}
          variant="contained"
          onClick={runImport}
        >
          Import tariff
        </Button>
      </Box>

      {!isNil(error) && (
        <Alert severity="error" sx={styles.section}>
          {error}
        </Alert>
      )}
    </Box>
  );

  if (isProd) {
    return <Box sx={styles.danger}>{content}</Box>;
  }

  return content;
};
